// =============================================================================
// composables/usePushNotifications.js — Web Push (VAPID) del lado cliente.
// -----------------------------------------------------------------------------
// - `enable()`  : pide permiso, se suscribe con la clave VAPID pública y guarda
//                 la suscripción en el backend (POST /push/subscribe).
// - `disable()` : avisa al backend y cancela la suscripción local.
// - `isSubscribed()`: estado real consultando el PushManager.
//
// La clave VAPID pública se obtiene de `VUE_APP_VAPID_PUBLIC_KEY` si existe; si
// no, del backend (GET /push/vapid), para no tener que recompilar al rotarla.
// =============================================================================
import { ref } from 'vue';
import apiClient from '@/api/client';

// base64url (VAPID) -> Uint8Array (applicationServerKey).
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const isSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// `navigator.serviceWorker.ready` no resuelve si no hay un SW activo; lo
// limitamos con un timeout para no colgar la UI (p. ej. en dev sin SW).
const getRegistration = async (timeoutMs = 4000) => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('service worker timeout')), timeoutMs)
      ),
    ]);
  } catch (e) {
    return null;
  }
};

export function usePushNotifications() {
  const supported = ref(isSupported());
  const permission = ref(isSupported() ? Notification.permission : 'unsupported');
  const busy = ref(false);
  const subscribed = ref(false);

  const fetchVapidKey = async () => {
    if (process.env.VUE_APP_VAPID_PUBLIC_KEY) {
      return process.env.VUE_APP_VAPID_PUBLIC_KEY;
    }
    const res = await apiClient.get('/push/vapid');
    return res?.data?.publicKey || '';
  };

  const refresh = async () => {
    if (!isSupported()) {
      subscribed.value = false;
      return false;
    }
    permission.value = Notification.permission;
    const registration = await getRegistration();
    if (!registration) {
      subscribed.value = false;
      return false;
    }
    const sub = await registration.pushManager.getSubscription();
    subscribed.value = !!sub;
    return subscribed.value;
  };

  const enable = async () => {
    if (!isSupported()) {
      throw new Error('Tu navegador no soporta notificaciones push.');
    }
    busy.value = true;
    try {
      const registration = await getRegistration();
      if (!registration) {
        throw new Error('El service worker aún no está listo. Intenta de nuevo en unos segundos.');
      }

      const perm = await Notification.requestPermission();
      permission.value = perm;
      if (perm !== 'granted') {
        throw new Error('Permiso de notificaciones denegado.');
      }

      const publicKey = await fetchVapidKey();
      if (!publicKey) {
        throw new Error('El servidor no tiene configuradas las notificaciones (VAPID).');
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      await apiClient.post('/push/subscribe', subscription.toJSON());
      subscribed.value = true;
      return true;
    } finally {
      busy.value = false;
    }
  };

  const disable = async () => {
    if (!isSupported()) return false;
    busy.value = true;
    try {
      const registration = await getRegistration();
      const subscription = registration
        ? await registration.pushManager.getSubscription()
        : null;

      if (subscription) {
        try {
          await apiClient.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        } catch (e) {
          // El backend puede no tener la suscripción; igual la cancelamos local.
        }
        await subscription.unsubscribe();
      }
      permission.value = Notification.permission;
      subscribed.value = false;
      return true;
    } finally {
      busy.value = false;
    }
  };

  return { supported, permission, busy, subscribed, enable, disable, refresh };
}
